import React, { useState } from "react";
import { Input, Button, Alert } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  signOut,
} from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import "./Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value) ? "" : "Invalid email format");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleSignUp = () => {
    if (!validateEmail(email) || password.length < 6 || password !== confirmPassword) {
      setEmailError(!validateEmail(email) ? "Invalid email format" : "");
      setPasswordError(password.length < 6 ? "Password must be at least 6 characters" : "");
      return;
    }

    createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        const user = userCredential.user;
        console.log("User signed up:", user);

        updateProfile(auth.currentUser, {
          displayName: email.split("@")[0], // Example: Use the first part of the email as the display name
        });

        sendEmailVerification(auth.currentUser)
          .then(() => {
            console.log("Verification email sent");
          })
          .catch((error) => {
            console.error("Error sending verification email:", error);
          });

        window.alert("Sign up successful. Please verify your email.");
        setIsSignUp(false); // Switch back to login mode
      })
      .catch((error) => {
        console.log("Sign up error:", error.message);
        window.alert("Sign up failed: " + error.message);
      });
  };

  const handleLogin = () => {
    if (!validateEmail(email) || !password) {
      window.alert("Please enter valid email and password");
      return;
    }

    signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        const user = userCredential.user;
        console.log("User logged in:", user);

        if (!user.emailVerified) {
          window.alert("Please verify your email before logging in.");
          return;
        }

        setUser(user);
        window.alert("Login successful");
        navigate("/checkout");
      })
      .catch((error) => {
        console.log("Login error:", error.message);
        window.alert("Login failed: " + error.message);
      });
  };

  const handleForgotPassword = () => {
    if (!validateEmail(email)) {
      window.alert("Please enter a valid email address");
      return;
    }

    sendPasswordResetEmail(auth, email)
      .then(() => {
        window.alert("Password reset email sent. Check your inbox.");
      })
      .catch((error) => {
        console.error("Error sending password reset email:", error);
        window.alert("Failed to send password reset email.");
      });
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        setUser(null);  // Clear user state if you're using context
        window.alert("Logout successful");
        navigate("/login");  // Redirect to login page
      })
      .catch((error) => {
        console.error("Logout error:", error.message);
        window.alert("Logout failed: " + error.message);
      });
  };

  return (
    <div className="login">
      <div className="login-container">
        <div className="logo-container">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/erps-cdea6.firebasestorage.app/o/got%2FGreen%20Professional%20Healthcare%20YouTube%20Thumbnail.png?alt=media&token=6a3647a0-a252-4de2-b990-a083941ba5c0"
            alt="Logo"
            className="logo"
          />
        </div>
        <div className="login-card">
          <Input
            prefix={<UserOutlined className="site-form-item-icon" />}
            placeholder="Email"
            onChange={handleEmailChange}
            value={email}
            className="login-input"
          />
          {emailError && <Alert message={emailError} type="error" showIcon />}
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            className="login-input"
          />
          {isSignUp && (
            <Input.Password
              prefix={<LockOutlined className="site-form-item-icon" />}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              className="login-input"
            />
          )}
          {passwordError && <Alert message={passwordError} type="error" showIcon />}
          <Button
            type="primary"
            className="login-button"
            onClick={isSignUp ? handleSignUp : handleLogin}
          >
            {isSignUp ? "Sign Up" : "Login"}
          </Button>
          {!isSignUp && (
            <Button type="link" className="forgot-password" onClick={handleForgotPassword}>
              Forgot Password?
            </Button>
          )}
          <Button
            type="link"
            className="toggle-signup"
            onClick={() => setIsSignUp(!isSignUp)}
          >
            {isSignUp ? "Already have an account? Login" : "Don't have an account? Sign Up"}
          </Button>

          {/* Logout button inside the card */}
          <Button
            type="danger"
            className="logout-button"
            onClick={handleLogout}
            style={{ marginTop: '20px' }}
          >
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Login;
